import { asList } from '../lib/api';
import { useState, useEffect, useCallback } from 'react';
import { holidaysApi, type Holiday } from '../lib/holidaysApi';

/** Admin page to manage the school holiday calendar. Parents see these under My family → Holidays. */
export function HolidaysAdminPage() {
  const [rows, setRows] = useState<Holiday[]>([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { holidaysApi.list(year).then((r) => setRows(asList(r))).catch(() => setRows([])); }, [year]);
  useEffect(load, [load]);

  const add = async () => {
    if (!name.trim() || !date) return;
    setBusy(true);
    try {
      await holidaysApi.create({ name: name.trim(), date, endDate: endDate || null, note: note.trim() || null, institutionId: null });
      setName(''); setDate(''); setEndDate(''); setNote(''); load();
    } catch { /* */ } finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this holiday?')) return;
    await holidaysApi.remove(id); load();
  };

  const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Calendar</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Holidays</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>Published to parents under My family → Holidays.</p>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 620 }}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Add a holiday</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <input className="input" placeholder="Name (e.g. Dasara)" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '2 1 200px' }} />
          <div>
            <label className="label" style={{ fontSize: 11 }}>From</label>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="label" style={{ fontSize: 11 }}>To (optional)</label>
            <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        <input className="input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} style={{ marginBottom: 10 }} />
        <button className="btn btn-accent" disabled={busy} onClick={add}>Add holiday</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <label className="label" style={{ margin: 0 }}>Year</label>
        <select className="input" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
          {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {rows.length === 0
        ? <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>No holidays for {year}.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((h) => (
              <div key={h.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{h.name}</div>
                  {h.note && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{h.note}</div>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{fmt(h.date)}{h.endDate && h.endDate !== h.date ? ` – ${fmt(h.endDate)}` : ''}</div>
                <button className="btn" onClick={() => remove(h.id)}>✕</button>
              </div>
            ))}
          </div>}
    </div>
  );
}
